/**
 * 72-Hour Overtime Monitoring — Excel Export (client-side, xlsx-js-style)
 *
 * Produces a branded, premium .xlsx aligned with the rest of the HR
 * Portal's exports (navy header band, zebra rows, double-ruled total
 * rows, confidentiality footer) — see ``unutilisedLeaveExport.js`` and
 * the backend openpyxl payroll register for the shared aesthetic.
 *
 * Supports the HR mental model directly:
 *   - "Export what I'm looking at" → a single month, honouring the
 *     active department / trade / status / discrepancy / search scope.
 *   - "Give me a few months in one file" → multiple months, each on its
 *     own sheet, fronted by a Summary sheet that lines the months up
 *     side-by-side so trends (rising OT, recurring breaches) are obvious
 *     at a glance without flipping tabs.
 *
 * Data shape per employee mirrors the ``/overtime-monitoring/employees/``
 * payload (see ``OvertimeMonitoringService.to_employees_payload``):
 *   staffId, fullName, department, trade, employmentClassification,
 *   currentOtHours, hoursRemaining, daysWithOt, percentageUsed,
 *   unifiedStatus, forecast{ projectedEomOt, projectedBreachDate }.
 */
import toast from './toast';
import { getLabelForStatus } from './statusConfig';
import { COMPANY_NAME, getGeneratedTimestamp } from './pdfReportTemplate';

const REPORT_TITLE = '72-Hour Overtime Monitoring';
const OT_CAP_HOURS = 72;

// ─── Palette (mirrors unutilisedLeaveExport.js / payroll register) ───────────

const _BRAND_NAVY = '1B2A4A';
const _WHITE = 'FFFFFF';
const _GRAY_50 = 'FAFAFA';
const _GRAY_200 = 'E5E7EB';
const _GRAY_700 = '374151';
const _GREEN_LIGHT = 'E2EFDA';

// Status-tier accents for the per-row Status cell — chosen to echo the
// on-screen StatusBadge tiers so the printout reads like the dashboard.
const _STATUS_FILL = {
  exceeded: 'F8D7DA',
  critical: 'F8D7DA',
  non_compliant: 'F8D7DA',
  projected_exceed: 'FFF0D6',
  at_risk: 'FFF0D6',
  watch: 'FFF0D6',
  on_track: 'E2EFDA',
  compliant: 'E2EFDA',
};
const _STATUS_TEXT = {
  exceeded: '842029',
  critical: '842029',
  non_compliant: '842029',
  projected_exceed: '7A5C00',
  at_risk: '7A5C00',
  watch: '7A5C00',
  on_track: '2C5E1A',
  compliant: '2C5E1A',
};

const THIN_BORDER = {
  top: { style: 'thin', color: { rgb: _GRAY_200 } },
  bottom: { style: 'thin', color: { rgb: _GRAY_200 } },
  left: { style: 'thin', color: { rgb: _GRAY_200 } },
  right: { style: 'thin', color: { rgb: _GRAY_200 } },
};

const TOTAL_BORDER = {
  top: { style: 'double', color: { rgb: _BRAND_NAVY } },
  bottom: { style: 'thin', color: { rgb: _GRAY_200 } },
  left: { style: 'thin', color: { rgb: _GRAY_200 } },
  right: { style: 'thin', color: { rgb: _GRAY_200 } },
};

const STYLE = {
  title: { font: { name: 'Calibri', sz: 14, bold: true, color: { rgb: _BRAND_NAVY } } },
  subtitle: { font: { name: 'Calibri', sz: 9, italic: true, color: { rgb: _GRAY_700 } } },
  metaLabel: { font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: _GRAY_700 } } },
  metaValue: { font: { name: 'Calibri', sz: 9, color: { rgb: '111827' } } },
  sectionHdr: { font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: _BRAND_NAVY } } },
  footer: { font: { name: 'Calibri', sz: 8, italic: true, color: { rgb: '9CA3AF' } } },
};

function headerStyle(halign = 'center') {
  return {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: _WHITE } },
    fill: { patternType: 'solid', fgColor: { rgb: _BRAND_NAVY } },
    alignment: { horizontal: halign, vertical: 'center', wrapText: true },
    border: THIN_BORDER,
  };
}

function dataStyle(halign, isEven, numFmt) {
  const s = {
    font: { name: 'Calibri', sz: 9, color: { rgb: '111827' } },
    fill: { patternType: 'solid', fgColor: { rgb: isEven ? _GRAY_50 : _WHITE } },
    alignment: { horizontal: halign || 'left', vertical: 'center' },
    border: THIN_BORDER,
  };
  if (numFmt) s.numFmt = numFmt;
  return s;
}

function statusStyle(status) {
  const fill = _STATUS_FILL[status] || _WHITE;
  const text = _STATUS_TEXT[status] || '111827';
  return {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: text } },
    fill: { patternType: 'solid', fgColor: { rgb: fill } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: THIN_BORDER,
  };
}

function totalStyle(halign, numFmt) {
  const s = {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: _BRAND_NAVY } },
    fill: { patternType: 'solid', fgColor: { rgb: _GREEN_LIGHT } },
    alignment: { horizontal: halign || 'right', vertical: 'center' },
    border: TOTAL_BORDER,
  };
  if (numFmt) s.numFmt = numFmt;
  return s;
}

function numOrBlank(v) {
  if (v == null || v === '') return '';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? '' : n;
}

function formatHoursMinutes(value) {
  if (value == null || value === '') return '';
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(n)) return '';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  let hours = Math.floor(abs);
  let minutes = Math.round((abs - hours) * 60);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }
  return minutes === 0
    ? `${sign}${hours}h`
    : `${sign}${hours}h ${minutes}m`;
}

function formatClassification(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.toLowerCase() === 'worker') return 'Worker';
  return raw
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function applyFreezePane(XL, ws, headerRow) {
  // Freeze the report header + column-header row, leaving the first data
  // row at the top when HR scrolls a long month. Uses xlsx-js-style's
  // worksheet freeze metadata shape; harmless if opened by readers that
  // ignore pane metadata.
  const ySplit = headerRow + 1;
  const topLeftCell = XL.utils.encode_cell({ r: ySplit, c: 0 });
  ws['!freeze'] = {
    xSplit: '0',
    ySplit: String(ySplit),
    topLeftCell,
    activePane: 'bottomLeft',
    state: 'frozen',
  };
}

let _xlsxStyleMod = null;
async function loadXlsxStyle() {
  if (!_xlsxStyleMod) {
    const mod = await import('xlsx-js-style');
    _xlsxStyleMod = mod.default || mod;
  }
  return _xlsxStyleMod;
}

// ─── Formatting helpers ──────────────────────────────────────────────────────

/** "2026-05" → "May 2026". Falls back to the raw string on bad input. */
export function formatMonthLabel(yearMonth) {
  if (!yearMonth) return '';
  try {
    const [y, m] = String(yearMonth).split('-');
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
  } catch (e) {
    return String(yearMonth);
  }
}

/** "2026-05" → "May2026" for filenames. */
function monthFilenameToken(yearMonth) {
  if (!yearMonth) return '';
  try {
    const [y, m] = String(yearMonth).split('-');
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString('en-SG', { month: 'short', year: 'numeric' }).replace(/\s+/g, '');
  } catch (e) {
    return String(yearMonth).replace(/-/g, '');
  }
}

/** "2026-05-21" → "21 May 2026". */
function fmtBreachDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (e) {
    return iso;
  }
}

/**
 * Build a human-readable scope label from the active table filters, so
 * the export carries the same context HR was looking at on screen.
 */
export function buildScopeLabel(filters = {}) {
  const parts = [];
  if (filters.department) parts.push(`Department: ${filters.department}`);
  if (filters.trade) parts.push(`Trade: ${filters.trade}`);
  if (filters.riskLevel) {
    const statusLabels = {
      action_required: 'Action Required',
      watch_list: 'Watch List',
    };
    parts.push(`Status: ${statusLabels[filters.riskLevel] || getLabelForStatus(filters.riskLevel)}`);
  }
  if (filters.hasDiscrepancy === 'true') parts.push('With undeclared OT');
  else if (filters.hasDiscrepancy === 'false') parts.push('No discrepancy');
  if (filters.search) parts.push(`Search: "${filters.search}"`);
  return parts.length ? parts.join('  ·  ') : 'All workmen';
}

// ─── Per-month aggregates (mirror deriveStatsFromEmployees) ──────────────────

function computeMonthStats(employees) {
  const list = employees || [];
  const total = list.length;
  let totalOt = 0;
  const by = {
    compliant: 0, on_track: 0, at_risk: 0, watch: 0,
    projected_exceed: 0, critical: 0, exceeded: 0,
  };
  for (const e of list) {
    totalOt += Number(e.currentOtHours) || 0;
    const key = e.unifiedStatus || e.riskLevel || 'compliant';
    if (key in by) by[key] += 1;
    else by.compliant += 1;
  }
  const okCount = by.compliant + by.on_track;
  totalOt = Math.round(totalOt * 100) / 100;
  return {
    total,
    totalOt,
    avgOt: total > 0 ? Math.round((totalOt / total) * 100) / 100 : 0,
    compliant: okCount,
    atRisk: by.at_risk + by.watch,
    critical: by.critical + by.projected_exceed,
    exceeded: by.exceeded,
    complianceRate: total > 0 ? Math.round((okCount / total) * 100) : 0,
  };
}

// ─── Detail-sheet column schema ──────────────────────────────────────────────

function detailColumns(includeForecast) {
  const cols = [
    { header: 'Staff ID', width: 12, align: 'left' },
    { header: 'Name', width: 26, align: 'left' },
    { header: 'Department', width: 22, align: 'left' },
    { header: 'Trade', width: 18, align: 'left' },
    { header: 'Classification', width: 14, align: 'center' },
    { header: 'OT Hours', width: 13, align: 'right' },
    { header: '% of 72h Cap', width: 12, align: 'right', numFmt: '0.0"%"' },
    { header: 'Hours Remaining', width: 16, align: 'right' },
    { header: 'Days w/ OT', width: 10, align: 'right', numFmt: '0' },
    { header: 'Status', width: 16, align: 'center', isStatus: true },
  ];
  if (includeForecast) {
    cols.push({ header: 'Projected EOM OT', width: 17, align: 'right' });
    cols.push({ header: 'Projected Breach', width: 16, align: 'center' });
  }
  return cols;
}

function employeeToRow(emp, includeForecast) {
  const status = emp.unifiedStatus || emp.riskLevel || 'compliant';
  const row = [
    emp.staffId || '',
    emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
    emp.department || 'Unassigned',
    emp.trade || 'No Trade',
    formatClassification(emp.employmentClassification),
    formatHoursMinutes(emp.currentOtHours),
    numOrBlank(emp.percentageUsed),
    formatHoursMinutes(emp.hoursRemaining),
    numOrBlank(emp.daysWithOt),
    getLabelForStatus(status),
  ];
  if (includeForecast) {
    const f = emp.forecast || null;
    row.push(f && f.projectedEomOt != null ? formatHoursMinutes(f.projectedEomOt) : '');
    row.push(f && f.projectedBreachDate ? fmtBreachDate(f.projectedBreachDate) : '');
  }
  return { row, status };
}

// ─── Sheet builders ──────────────────────────────────────────────────────────

function buildSummarySheet(XL, months, scopeLabel) {
  const aoa = [];
  aoa.push([COMPANY_NAME]);                                   // 0
  aoa.push([`${REPORT_TITLE} — Compliance Export`]);          // 1
  aoa.push([]);                                               // 2
  aoa.push(['Generated', getGeneratedTimestamp()]);           // 3
  aoa.push(['Scope', scopeLabel || 'All workmen']);           // 4
  aoa.push(['Months', months.map((m) => m.label).join(', ')]);// 5
  aoa.push(['MOM Cap', `${OT_CAP_HOURS} hours / calendar month (Employment Act Part IV)`]); // 6
  aoa.push([]);                                               // 7

  const headerRow = aoa.length;                               // 8
  const colHeaders = [
    'Month', 'Workmen', 'Total OT', 'Avg OT',
    'OK (<48h)', 'At Risk', 'Critical', 'Exceeded', 'Compliance %',
  ];
  aoa.push(colHeaders);

  const statList = months.map((m) => computeMonthStats(m.employees));
  statList.forEach((s, i) => {
    aoa.push([
      months[i].label,
      s.total,
      formatHoursMinutes(s.totalOt),
      formatHoursMinutes(s.avgOt),
      s.compliant,
      s.atRisk,
      s.critical,
      s.exceeded,
      numOrBlank(s.complianceRate),
    ]);
  });

  // Combined row (Σ across months) when more than one month is present.
  // Status counts are person-months (a worker counts once per month),
  // which is the correct denominator for a multi-month compliance view.
  let totalRowIdx = -1;
  if (months.length > 1) {
    const sum = statList.reduce(
      (a, s) => ({
        total: a.total + s.total,
        totalOt: a.totalOt + s.totalOt,
        compliant: a.compliant + s.compliant,
        atRisk: a.atRisk + s.atRisk,
        critical: a.critical + s.critical,
        exceeded: a.exceeded + s.exceeded,
      }),
      { total: 0, totalOt: 0, compliant: 0, atRisk: 0, critical: 0, exceeded: 0 },
    );
    const combinedTotalOt = Math.round(sum.totalOt * 100) / 100;
    const combinedAvg = sum.total > 0 ? Math.round((combinedTotalOt / sum.total) * 100) / 100 : 0;
    const combinedRate = sum.total > 0 ? Math.round((sum.compliant / sum.total) * 100) : 0;
    totalRowIdx = aoa.length;
    aoa.push([
      'Combined (Σ months)',
      sum.total,
      formatHoursMinutes(combinedTotalOt),
      formatHoursMinutes(combinedAvg),
      sum.compliant,
      sum.atRisk,
      sum.critical,
      sum.exceeded,
      numOrBlank(combinedRate),
    ]);
  }

  aoa.push([]);
  const footerText = `This is a computer-generated report. Generated by WEPL HR Portal on ${getGeneratedTimestamp()}. Confidential — for authorised HR use only.`;
  aoa.push([footerText]);
  const footerRowIdx = aoa.length - 1;

  const ws = XL.utils.aoa_to_sheet(aoa);
  const nCols = colHeaders.length;
  ws['!cols'] = [
    { wch: 22 }, { wch: 10 }, { wch: 14 }, { wch: 12 },
    { wch: 11 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 13 },
  ];
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: nCols - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: nCols - 1 } },
    { s: { r: footerRowIdx, c: 0 }, e: { r: footerRowIdx, c: nCols - 1 } },
  ];

  // Title block styles
  ws['A1'].s = STYLE.title;
  ws['A2'].s = STYLE.subtitle;
  [3, 4, 5, 6].forEach((r) => {
    const a = XL.utils.encode_cell({ r, c: 0 });
    const b = XL.utils.encode_cell({ r, c: 1 });
    if (ws[a]) ws[a].s = STYLE.metaLabel;
    if (ws[b]) ws[b].s = STYLE.metaValue;
  });

  // Comparison-table header
  const aligns = ['left', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right'];
  const fmts = [null, '0', null, null, '0', '0', '0', '0', '0"%"'];
  for (let c = 0; c < nCols; c++) {
    const ref = XL.utils.encode_cell({ r: headerRow, c });
    if (ws[ref]) ws[ref].s = headerStyle(aligns[c]);
  }
  for (let i = 0; i < months.length; i++) {
    const rowIdx = headerRow + 1 + i;
    for (let c = 0; c < nCols; c++) {
      const ref = XL.utils.encode_cell({ r: rowIdx, c });
      if (ws[ref]) ws[ref].s = dataStyle(aligns[c], i % 2 === 0, fmts[c]);
    }
  }
  if (totalRowIdx >= 0) {
    for (let c = 0; c < nCols; c++) {
      const ref = XL.utils.encode_cell({ r: totalRowIdx, c });
      if (ws[ref]) ws[ref].s = totalStyle(c === 0 ? 'left' : 'right', fmts[c]);
    }
  }

  const fRef = XL.utils.encode_cell({ r: footerRowIdx, c: 0 });
  if (ws[fRef]) ws[fRef].s = STYLE.footer;
  applyFreezePane(XL, ws, headerRow);

  return ws;
}

function buildMonthSheet(XL, month, scopeLabel, includeForecast) {
  const cols = detailColumns(includeForecast);
  const nCols = cols.length;
  const stats = computeMonthStats(month.employees);

  const aoa = [];
  aoa.push([`${month.label} — ${REPORT_TITLE}`]);            // 0 title
  aoa.push([
    `Workmen: ${stats.total}   ·   Total OT: ${formatHoursMinutes(stats.totalOt)}   ·   `
    + `Avg: ${formatHoursMinutes(stats.avgOt)}   ·   Compliance: ${stats.complianceRate}%`
    + (month.isCurrentMonth ? '   ·   (current month — live preview + forecast)' : ''),
  ]);                                                         // 1 subtitle
  aoa.push([`Scope: ${scopeLabel || 'All workmen'}`]);       // 2
  aoa.push([]);                                               // 3

  const headerRow = aoa.length;                               // 4
  aoa.push(cols.map((c) => c.header));

  // Sort: most severe first, then highest OT (matches dashboard default).
  const sevOrder = {
    exceeded: 6, critical: 5, projected_exceed: 4, at_risk: 3,
    watch: 2, on_track: 1, compliant: 0,
  };
  const sorted = [...(month.employees || [])].sort((a, b) => {
    const sa = sevOrder[a.unifiedStatus || a.riskLevel] ?? 0;
    const sb = sevOrder[b.unifiedStatus || b.riskLevel] ?? 0;
    if (sb !== sa) return sb - sa;
    return (Number(b.currentOtHours) || 0) - (Number(a.currentOtHours) || 0);
  });

  const rowMeta = [];
  sorted.forEach((emp, i) => {
    const { row, status } = employeeToRow(emp, includeForecast);
    aoa.push(row);
    rowMeta.push({ isEven: i % 2 === 0, status });
  });

  // Total row
  const totalArr = new Array(nCols).fill('');
  totalArr[0] = `Total — ${stats.total} workmen`;
  totalArr[5] = formatHoursMinutes(stats.totalOt);
  const totalRowIdx = aoa.length;
  aoa.push(totalArr);

  aoa.push([]);
  const footerText = `This is a computer-generated report. Generated by WEPL HR Portal on ${getGeneratedTimestamp()}. Confidential — for authorised HR use only.`;
  const footerArr = new Array(nCols).fill('');
  footerArr[0] = footerText;
  aoa.push(footerArr);
  const footerRowIdx = aoa.length - 1;

  const ws = XL.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map((c) => ({ wch: c.width }));
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: nCols - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: nCols - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: nCols - 1 } },
    { s: { r: footerRowIdx, c: 0 }, e: { r: footerRowIdx, c: nCols - 1 } },
  ];

  ws['!autofilter'] = {
    ref: `${XL.utils.encode_cell({ r: headerRow, c: 0 })}:${XL.utils.encode_cell({ r: totalRowIdx - 1, c: nCols - 1 })}`,
  };

  ws['A1'].s = STYLE.sectionHdr;
  if (ws['A2']) ws['A2'].s = STYLE.subtitle;
  if (ws['A3']) ws['A3'].s = STYLE.metaLabel;

  for (let c = 0; c < nCols; c++) {
    const ref = XL.utils.encode_cell({ r: headerRow, c });
    if (ws[ref]) ws[ref].s = headerStyle(cols[c].align);
  }

  rowMeta.forEach((meta, di) => {
    const rowIdx = headerRow + 1 + di;
    for (let c = 0; c < nCols; c++) {
      const ref = XL.utils.encode_cell({ r: rowIdx, c });
      if (!ws[ref]) ws[ref] = { v: '', t: 's' };
      if (cols[c].isStatus) {
        ws[ref].s = statusStyle(meta.status);
      } else {
        ws[ref].s = dataStyle(cols[c].align, meta.isEven, cols[c].numFmt);
      }
    }
  });

  for (let c = 0; c < nCols; c++) {
    const ref = XL.utils.encode_cell({ r: totalRowIdx, c });
    if (!ws[ref]) ws[ref] = { v: '', t: 's' };
    ws[ref].s = totalStyle(c === 0 ? 'left' : 'right', cols[c].numFmt);
  }

  const fRef = XL.utils.encode_cell({ r: footerRowIdx, c: 0 });
  if (ws[fRef]) ws[fRef].s = STYLE.footer;
  applyFreezePane(XL, ws, headerRow);

  return ws;
}

// Excel sheet names: max 31 chars, no : \ / ? * [ ]. Dedupe collisions.
function safeSheetName(name, used) {
  let base = String(name).replace(/[:\\/?*[\]]/g, ' ').slice(0, 31).trim() || 'Sheet';
  let candidate = base;
  let n = 2;
  while (used.has(candidate)) {
    const suffix = ` (${n})`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    n += 1;
  }
  used.add(candidate);
  return candidate;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Build and download the workbook for one or more months.
 *
 * @param {Object}   args
 * @param {Array}    args.months       [{ yearMonth, isCurrentMonth, employees, label? }]
 * @param {string}   [args.scopeLabel] Human description of the active filters.
 */
export async function exportOvertimeMonitoringWorkbook({ months, scopeLabel = '' }) {
  const monthList = (months || [])
    .filter((m) => m && m.yearMonth)
    .map((m) => ({
      yearMonth: m.yearMonth,
      isCurrentMonth: !!m.isCurrentMonth,
      employees: m.employees || [],
      label: m.label || formatMonthLabel(m.yearMonth),
    }))
    // Newest month first — matches the month picker / dashboard ordering.
    .sort((a, b) => (a.yearMonth < b.yearMonth ? 1 : -1));

  if (monthList.length === 0) {
    toast.error('Nothing to export — no months selected.');
    return;
  }

  const totalRows = monthList.reduce((s, m) => s + m.employees.length, 0);
  if (totalRows === 0) {
    toast.error('No workmen match the current scope for the selected months.');
    return;
  }

  const XL = await loadXlsxStyle();
  const includeForecast = monthList.some((m) => m.isCurrentMonth);
  const wb = XL.utils.book_new();
  const usedNames = new Set();

  // Summary sheet — only worth its own tab when comparing months. For a
  // single month it would just duplicate the sheet header, so skip it.
  if (monthList.length > 1) {
    const summary = buildSummarySheet(XL, monthList, scopeLabel);
    XL.utils.book_append_sheet(wb, summary, safeSheetName('Summary', usedNames));
  }

  monthList.forEach((month) => {
    const ws = buildMonthSheet(XL, month, scopeLabel, includeForecast);
    XL.utils.book_append_sheet(wb, ws, safeSheetName(month.label, usedNames));
  });

  const tokens = monthList.map((m) => monthFilenameToken(m.yearMonth));
  const span = tokens.length === 1 ? tokens[0] : `${tokens[tokens.length - 1]}-${tokens[0]}`;
  const filename = `OvertimeMonitoring_${span}.xlsx`;

  XL.writeFile(wb, filename);
  toast.success(
    monthList.length > 1
      ? `Excel downloaded — ${monthList.length} months`
      : 'Excel downloaded',
  );
}

/**
 * Single-month convenience wrapper — exports the current on-screen view.
 * Kept for the table's "Export" quick action.
 */
export async function exportOvertimeMonitoringExcel({
  employees,
  yearMonth,
  isCurrentMonth = true,
  scopeLabel = '',
}) {
  return exportOvertimeMonitoringWorkbook({
    months: [{ yearMonth, isCurrentMonth, employees }],
    scopeLabel,
  });
}
